import { Component, Input, OnInit } from '@angular/core';
import { ToastService } from '@teorema/patron';
import { FindOptions } from 'src/app/models/find-options.model';
import { WebLicense, WebLicenseSummaryItem } from 'src/app/models/web-license.model';
import { WebLicenseCustomerService } from 'src/app/shared/services';
import {
  webLicenseSessionLabel,
  webLicenseStatusClass,
  webLicenseStatusLabel,
  webLicenseSystemName,
} from 'src/app/shared/utils/web-license.utils';

/** Chamado 0000577675 (tarefa 16) — licenças do usuário no cadastro, pelas rotas do cliente. */
@Component({
  selector: 'bw-user-licenses',
  templateUrl: './bw-user-licenses.component.html',
  styleUrls: ['../styles/detail-grid.scss'],
  standalone: false,
})
export class BwUserLicensesComponent implements OnInit {
  @Input() bwUserId?: string;
  @Input() userName?: string;

  initialized = false;
  loading = false;
  licenses: WebLicense[] = [];
  summary: WebLicenseSummaryItem[] = [];

  selectedSystemId: number | '' = '';
  linking = false;

  confirmTarget?: WebLicense;
  confirmLoading = false;

  readonly findOptions: FindOptions = {
    fields: [
      { label: 'Sistema' },
      { label: 'Assento' },
      { label: 'Apelido' },
      { label: 'Status' },
      { label: 'Em uso' },
      { label: 'Ações' },
    ],
  };

  constructor(
    private licenseService: WebLicenseCustomerService,
    private toastService: ToastService
  ) {}

  get canWrite(): boolean {
    return this.licenseService.canWrite();
  }

  /** Sistemas com assento livre em que o usuário ainda não tem licença (uma por sistema). */
  get linkableSystems(): WebLicenseSummaryItem[] {
    const owned = new Set(this.licenses.map(license => license.vendor_system_id));
    return this.summary.filter(item => item.available > 0 && !owned.has(item.vendor_system_id));
  }

  ngOnInit(): void {
    this.init();
  }

  async init() {
    if (this.initialized) return;
    this.initialized = true;
    await this.load();
  }

  async load() {
    if (!this.bwUserId) return;

    this.loading = true;

    try {
      const [summary, response] = await Promise.all([
        this.licenseService.summary(),
        this.licenseService.list({ bw_user_id: this.bwUserId, size: 100 }),
      ]);
      this.summary = summary;
      this.licenses = response?.data ?? [];
      if (!this.linkableSystems.some(item => item.vendor_system_id === this.selectedSystemId)) {
        this.selectedSystemId = '';
      }
    } catch (error) {
      this.summary = [];
      this.licenses = [];
      this.toastService.show({
        color: 'error',
        message: 'Houve um erro ao carregar as licenças do usuário',
      });
    } finally {
      this.loading = false;
    }
  }

  readonly systemName = webLicenseSystemName;
  readonly statusLabel = webLicenseStatusLabel;
  readonly statusClass = webLicenseStatusClass;
  readonly sessionLabel = webLicenseSessionLabel;

  /**
   * Vincula um assento livre do sistema escolhido. O `only_available` do back não olha `active`,
   * então a escolha fica aqui: assento ativo, normal antes de cortesia.
   */
  async link() {
    if (!this.bwUserId || this.selectedSystemId === '') return;

    this.linking = true;

    try {
      const response = await this.licenseService.list({
        vendor_system_id: [this.selectedSystemId],
        only_available: true,
        size: 50,
      });
      const candidates = (response?.data ?? []).filter(license => license.active);
      const seat = candidates.find(license => !license.demo) ?? candidates[0];

      if (!seat) {
        this.toastService.show({
          color: 'warn',
          message: 'Nenhum assento livre ativo neste sistema. Fale com o suporte da Teorema.',
        });
        return;
      }

      await this.licenseService.link(seat.id, this.bwUserId);
      this.toastService.show({ color: 'success', message: 'Licença vinculada ao usuário' });
      this.selectedSystemId = '';
      await this.load();
    } catch (error: any) {
      const errors = error?.error?.errors;
      this.toastService.show({
        color: 'error',
        message:
          errors?.bw_user_id?.[0] ??
          errors?.status?.[0] ??
          error?.error?.message ??
          'Não foi possível vincular a licença',
      });
    } finally {
      this.linking = false;
    }
  }

  askUnlink(license: WebLicense) {
    this.confirmTarget = license;
  }

  async unlink() {
    if (!this.confirmTarget) return;

    this.confirmLoading = true;

    try {
      await this.licenseService.unlink(this.confirmTarget.id);
      this.toastService.show({ color: 'success', message: 'Licença desvinculada do usuário' });
      this.confirmTarget = undefined;
      await this.load();
    } catch (error: any) {
      this.toastService.show({
        color: 'error',
        message: error?.error?.message ?? 'Não foi possível desvincular a licença',
      });
    } finally {
      this.confirmLoading = false;
    }
  }
}
